import type { ZodType } from "zod";
import z from "zod";

import {
  acceptsExactly,
  IsoDateSchema,
  type IsoDateUnbranded,
} from "@thalamos/common";

import {
  PersonInvolvementKindSchema,
  ViewRouteSchema,
  type PersonInvolvementKind,
  type ViewRouteUnbranded,
} from "./involvement.js";
import {
  OrganisationIdSchema,
  OrganisationSchema,
  type OrganisationIdUnbranded,
  type OrganisationUnbranded,
} from "./organisation.js";
import { PersonRecordSchema, type PersonRecordUnbranded } from "./person.js";
import { PersonStateSchema, type PersonState } from "./personState.js";
import {
  UserIdSchema,
  UserMembershipSchema,
  UserSchema,
  type UserIdUnbranded,
  type UserMembershipUnbranded,
  type UserUnbranded,
} from "./user.js";

/**
 * How many rows one page of a listing may carry.
 *
 * A ceiling rather than only a default, because these listings span every
 * organisation: without one, a single request is a copy of the table. Stated here
 * so the client cannot ask for more than the server will give and then paginate
 * against a number it never receives.
 */
export const DIRECTORY_PAGE_LIMIT_MAX = 100;
export const DIRECTORY_PAGE_LIMIT_DEFAULT = 25;

/**
 * A page of a listing, and how many rows there are in total.
 *
 * `total` counts the whole result set rather than the page, which is what a pager
 * needs to say "page 2 of 9".
 *
 * Written out per collection rather than as one generic type: tsoa reads
 * TypeScript declarations to build the API contract and resolves a generic
 * inconsistently, so each page is its own named shape.
 */
export type UserDirectoryPageUnbranded = {
  results: UserUnbranded[];
  total: number;
};

export const UserDirectoryPageSchema = z.object({
  results: z.array(UserSchema),
  total: z.number().int().nonnegative(),
}) satisfies ZodType<UserDirectoryPageUnbranded>;

export type UserDirectoryPage = z.infer<typeof UserDirectoryPageSchema>;

export type OrganisationDirectoryPageUnbranded = {
  results: OrganisationUnbranded[];
  total: number;
};

export const OrganisationDirectoryPageSchema = z.object({
  results: z.array(OrganisationSchema),
  total: z.number().int().nonnegative(),
}) satisfies ZodType<OrganisationDirectoryPageUnbranded>;

export type OrganisationDirectoryPage = z.infer<
  typeof OrganisationDirectoryPageSchema
>;

export type ListedOrganisationUnbranded = {
  organisationId: OrganisationIdUnbranded;
  name: string;
};

export type ListedUserUnbranded = {
  userId: UserIdUnbranded;
  name: string;
};

export type PersonListingDetailsUnbranded = {
  dutyOfCare: ListedOrganisationUnbranded | null;
  placement: ListedOrganisationUnbranded | null;
  /** Whoever runs the case. */
  caseManager: ListedUserUnbranded | null;
  state: PersonState;
  /** Whether the session's organisation administers the person (`personActionsFromOrganisationAdministration`). */
  administered: boolean;
  /** When a definitive assessment about the young person was last approved. */
  lastApprovedAt: Date | null;
  /**
   * Their Unique Pupil Number, from the listed record or one merged into it.
   * Shown as stored rather than parsed as a `Upn`, so one stored value failing
   * its check cannot fail the whole listing.
   */
  upn: string | null;
};

const ListedOrganisationSchema = z.object({
  organisationId: OrganisationIdSchema,
  name: z.string(),
}) satisfies ZodType<ListedOrganisationUnbranded>;

/** A body named in a listing row, by id and name rather than the whole organisation. */
export type ListedOrganisation = z.infer<typeof ListedOrganisationSchema>;

const ListedUserSchema = z.object({
  userId: UserIdSchema,
  name: z.string(),
}) satisfies ZodType<ListedUserUnbranded>;

/** A person named in a listing row, by id and name rather than the whole account. */
export type ListedUser = z.infer<typeof ListedUserSchema>;

const PersonListingDetailsSchema = z.object({
  dutyOfCare: ListedOrganisationSchema.nullable(),
  placement: ListedOrganisationSchema.nullable(),
  caseManager: ListedUserSchema.nullable(),
  state: PersonStateSchema,
  administered: z.boolean(),
  lastApprovedAt: z.coerce.date().nullable(),
  upn: z.string().nullable(),
}) satisfies ZodType<PersonListingDetailsUnbranded>;

/**
 * What a listing row says about a young person's case, read from the whole
 * merge tree.
 */
export type PersonListingDetails = z.infer<typeof PersonListingDetailsSchema>;

/**
 * One young person in a page of the directory, with every route by which the
 * session reaches them and what their case holds.
 */
export type PersonDirectoryEntryUnbranded = PersonRecordUnbranded &
  PersonListingDetailsUnbranded & {
    routes: ViewRouteUnbranded[];
  };

export const PersonDirectoryEntrySchema = PersonRecordSchema.extend({
  routes: z.array(ViewRouteSchema),
  ...PersonListingDetailsSchema.shape,
}) satisfies ZodType<PersonDirectoryEntryUnbranded>;

export type PersonDirectoryEntry = z.infer<typeof PersonDirectoryEntrySchema>;

/**
 * A page of young people's records. Only records that still stand on their own
 * are listed — one that has been merged into another is reached through the
 * record that survived it.
 */
export type PersonDirectoryPageUnbranded = {
  results: PersonDirectoryEntryUnbranded[];
  total: number;
  /** Present when the request asked for them. */
  facets?: PersonListingFacetsUnbranded;
  /** Present when the request asked for it. */
  unassigned?: ListingUnassignedCountUnbranded;
};

export const PersonDirectoryPageSchema = z.object({
  results: z.array(PersonDirectoryEntrySchema),
  total: z.number().int().nonnegative(),
  facets: z.lazy(() => PersonListingFacetsSchema).optional(),
  unassigned: z.lazy(() => ListingUnassignedCountSchema).optional(),
}) satisfies ZodType<PersonDirectoryPageUnbranded>;

export type PersonDirectoryPage = z.infer<typeof PersonDirectoryPageSchema>;

/** The columns the young people's listing can be ordered by. */
export type PersonListingSortColumn =
  | "name"
  | "dateOfBirth"
  | "dutyOfCare"
  | "placement"
  | "involvement"
  | "caseManager"
  | "lastApproved";

export type SortDirection = "ascending" | "descending";

export type PersonListingSortUnbranded = {
  column: PersonListingSortColumn;
  direction: SortDirection;
};

const PersonListingSortSchema = acceptsExactly<PersonListingSortUnbranded>()(
  z.object({
    column: z.enum([
      "name",
      "dateOfBirth",
      "dutyOfCare",
      "placement",
      "involvement",
      "caseManager",
      "lastApproved",
    ]) satisfies ZodType<PersonListingSortColumn>,
    direction: z.enum([
      "ascending",
      "descending",
    ]) satisfies ZodType<SortDirection>,
  }),
);

export type PersonListingSort = z.infer<typeof PersonListingSortSchema>;

/** Alphabetical by the name each young person is shown under. */
export const DEFAULT_LISTING_SORT: PersonListingSort = {
  column: "name",
  direction: "ascending",
};

export type ListingDateRangeFilterUnbranded = {
  from: IsoDateUnbranded | null;
  to: IsoDateUnbranded | null;
  includeUnknown: boolean;
};

export type ListingOrganisationFilterUnbranded = {
  /** @maxItems 100 */
  organisationIds: OrganisationIdUnbranded[];
  includeNotAssigned: boolean;
};

export type ListingUserFilterUnbranded = {
  /** @maxItems 100 */
  userIds: UserIdUnbranded[];
  includeNotAssigned: boolean;
};

export type ListingInvolvementFilterUnbranded = {
  /** @maxItems 20 */
  kinds: PersonInvolvementKind[];
  /** Those the session sees only through its organisation. */
  includeViewerThroughOrganisation: boolean;
};

/** One optional filter per column; a column left out is not filtered. */
export type PersonListingFiltersUnbranded = {
  dateOfBirth?: ListingDateRangeFilterUnbranded;
  dutyOfCare?: ListingOrganisationFilterUnbranded;
  placement?: ListingOrganisationFilterUnbranded;
  involvement?: ListingInvolvementFilterUnbranded;
  caseManager?: ListingUserFilterUnbranded;
  lastApproved?: ListingDateRangeFilterUnbranded;
};

/** How many of the most-listed holders are offered per column; a ticked one the search found is offered besides. */
export const LISTING_FACET_LIMIT = 100;

/**
 * How many holders one filter may tick. A holder filter's `@maxItems` cannot
 * name this, so it has to be changed with it.
 */
export const LISTING_TICK_LIMIT = 100;

/**
 * The longest `PersonSearchRequest` query, in characters. Each word typed
 * becomes a condition of its own, so the bound caps how many conditions one
 * request puts to the database. `PersonSearchRequestUnbranded.query`'s
 * `@maxLength` cannot name this, so it has to be changed with it.
 */
export const personSearchQueryMaxLength = 200;

const ListingDateRangeFilterSchema = z
  .object({
    from: IsoDateSchema.nullable(),
    to: IsoDateSchema.nullable(),
    includeUnknown: z.boolean(),
  })
  .refine(
    ({ from, to }) => from === null || to === null || from <= to,
    "A range must not end before it starts",
  ) satisfies ZodType<ListingDateRangeFilterUnbranded>;

/**
 * Whole days, either end open, both inclusive. `includeUnknown` adds the rows
 * with no date at all, which a range on its own leaves out.
 */
export type ListingDateRangeFilter = z.infer<
  typeof ListingDateRangeFilterSchema
>;

const ListingOrganisationFilterSchema = z.object({
  organisationIds: z.array(OrganisationIdSchema).max(LISTING_TICK_LIMIT),
  includeNotAssigned: z.boolean(),
}) satisfies ZodType<ListingOrganisationFilterUnbranded>;

export type ListingOrganisationFilter = z.infer<
  typeof ListingOrganisationFilterSchema
>;

export const PersonListingFiltersSchema = z.object({
  dateOfBirth: ListingDateRangeFilterSchema.optional(),
  dutyOfCare: ListingOrganisationFilterSchema.optional(),
  placement: ListingOrganisationFilterSchema.optional(),
  involvement: z
    .object({
      kinds: z.array(PersonInvolvementKindSchema).max(20),
      includeViewerThroughOrganisation: z.boolean(),
    })
    .optional(),
  caseManager: z
    .object({
      userIds: z.array(UserIdSchema).max(LISTING_TICK_LIMIT),
      includeNotAssigned: z.boolean(),
    })
    .optional(),
  lastApproved: ListingDateRangeFilterSchema.optional(),
}) satisfies ZodType<PersonListingFiltersUnbranded>;

export type PersonListingFilters = z.infer<typeof PersonListingFiltersSchema>;

export type PersonSearchRequestUnbranded = {
  // `@maxLength` is kept equal to `personSearchQueryMaxLength`, which the schema enforces.
  /**
   * Matches a record in the young person's merge tree with a name holding every word typed, or an identifier equal to the whole text.
   * @maxLength 200
   */
  query?: string;
  limit?: number;
  offset?: number;
  sort?: PersonListingSortUnbranded;
  filters?: PersonListingFiltersUnbranded;
  /**
   * Only the unassigned profiles the session can act on: those whose case it
   * runs, or, when it is administering, those its organisation administers.
   */
  onlyUnassigned?: boolean;
  /** Whether to count each filter's options; the counts cost a pass over the whole reach. */
  withFacets?: boolean;
  /** Whether to say if the unassigned toggle is offered, and how many it would list. */
  withUnassignedCount?: boolean;
};

export const PersonSearchRequestSchema =
  acceptsExactly<PersonSearchRequestUnbranded>()(
    z
      .object({
        query: z.string().max(personSearchQueryMaxLength).optional(),
        limit: z.number().optional(),
        offset: z.number().optional(),
        sort: PersonListingSortSchema.optional(),
        filters: PersonListingFiltersSchema.optional(),
        onlyUnassigned: z.boolean().optional(),
        withFacets: z.boolean().optional(),
        withUnassignedCount: z.boolean().optional(),
      })
      .brand<"haven-common:PersonSearchRequest">(),
  );

/** What a search of the young people's directory asks for. */
export type PersonSearchRequest = z.infer<typeof PersonSearchRequestSchema>;

export type ListingUnassignedCountUnbranded = {
  offered: boolean;
  count: number;
};

export const ListingUnassignedCountSchema = z.object({
  offered: z.boolean(),
  count: z.number().int().nonnegative(),
}) satisfies ZodType<ListingUnassignedCountUnbranded>;

/**
 * The unassigned toggle: whether it is offered, judged over the whole reach,
 * and how many it would list given the search and filters.
 */
export type ListingUnassignedCount = z.infer<
  typeof ListingUnassignedCountSchema
>;

export type ListingOrganisationFacetUnbranded = ListedOrganisationUnbranded & {
  count: number;
};

export type ListingUserFacetUnbranded = ListedUserUnbranded & {
  count: number;
};

/**
 * How many young people each filter option would give, over the whole
 * listing. Each column's counts apply the search, the toggle and every other
 * column's filter, but not its own, so ticking a second option never shows 0.
 */
export type PersonListingFacetsUnbranded = {
  dateOfBirth: { unknown: number };
  dutyOfCare: {
    organisations: ListingOrganisationFacetUnbranded[];
    notAssigned: number;
  };
  placement: {
    organisations: ListingOrganisationFacetUnbranded[];
    notAssigned: number;
  };
  involvement: {
    kinds: { kind: PersonInvolvementKind; count: number }[];
    viewerThroughOrganisation: number;
  };
  caseManager: {
    users: ListingUserFacetUnbranded[];
    notAssigned: number;
  };
  lastApproved: { none: number };
};

const ListingOrganisationFacetSchema = z.object({
  organisationId: OrganisationIdSchema,
  name: z.string(),
  count: z.number().int().nonnegative(),
}) satisfies ZodType<ListingOrganisationFacetUnbranded>;

const count = z.number().int().nonnegative();

const ListingUserFacetSchema = z.object({
  userId: UserIdSchema,
  name: z.string(),
  count,
}) satisfies ZodType<ListingUserFacetUnbranded>;

export const PersonListingFacetsSchema = z.object({
  dateOfBirth: z.object({ unknown: count }),
  dutyOfCare: z.object({
    organisations: z.array(ListingOrganisationFacetSchema),
    notAssigned: count,
  }),
  placement: z.object({
    organisations: z.array(ListingOrganisationFacetSchema),
    notAssigned: count,
  }),
  involvement: z.object({
    kinds: z.array(z.object({ kind: PersonInvolvementKindSchema, count })),
    viewerThroughOrganisation: count,
  }),
  caseManager: z.object({
    users: z.array(ListingUserFacetSchema),
    notAssigned: count,
  }),
  lastApproved: z.object({ none: count }),
}) satisfies ZodType<PersonListingFacetsUnbranded>;

export type PersonListingFacets = z.infer<typeof PersonListingFacetsSchema>;

/**
 * The shortest search term the user search accepts.
 *
 * The search spans every organisation, so a term is what stops the workforce
 * being enumerable — see
 * `docs/decisions/2026-09-14-a-case-manager-may-look-anybody-up-to-involve-them.md`.
 * Two characters rather than three because "Ng", "Li" and "Wu" are whole
 * surnames, and a floor that hides them would make the people it excludes
 * unfindable rather than merely harder to sweep for.
 */
export const USER_FIND_MIN_TERM_LENGTH = 2;

/**
 * Somebody who could be named to a young person's record: who they are, and the
 * bodies behind them.
 *
 * Deliberately narrower than `User`, which carries the email address. A picker
 * needs to tell two people called J Smith apart, and the organisation does that
 * — so the address is absent from the type rather than dropped by each caller.
 *
 * The memberships are on the row rather than fetched per candidate, because
 * they are also what naming is judged against: which involvements somebody is
 * eligible for, and whether the body they would be named under is part of this
 * child's care.
 */
export type FoundUserUnbranded = {
  id: UserIdUnbranded;
  name: string;
  memberships: UserMembershipUnbranded[];
};

export const FoundUserSchema = z.object({
  id: UserIdSchema,
  name: z.string().min(1),
  memberships: z.array(UserMembershipSchema),
}) satisfies ZodType<FoundUserUnbranded>;

export type FoundUser = z.infer<typeof FoundUserSchema>;

export type FoundUsersPageUnbranded = {
  results: FoundUserUnbranded[];
  total: number;
};

export const FoundUsersPageSchema = z.object({
  results: z.array(FoundUserSchema),
  total: z.number().int().nonnegative(),
}) satisfies ZodType<FoundUsersPageUnbranded>;

export type FoundUsersPage = z.infer<typeof FoundUsersPageSchema>;
